<template>
  <nav :class="['navbar', { 'navbar-fixed': isSticky }]">
    <div class="container-fluid justify-content-between align-items-center">
      <!-- Logo -->
      <router-link
        class="navbar-brand"
        to="/"
        aria-label="Ir a Inicio"
        @click="closeMenu"
      >
        <img src="../assets/frenkel-logo.svg" alt="Frenkel Logo" class="logo_navbar" />
      </router-link>

      <div class="d-flex align-items-center">
        <div class="language-selector">
          <img src="../assets/images/languages.svg" alt="Idioma" class="language-icon" />
          <select
            :value="selectedLang"
            class="form-select lang-dropdown"
            @change="handleLanguageChange"
          >
            <option v-for="(label, code) in labelMap" :key="code" :value="code">
              {{ label }}
            </option>
          </select>
        </div>

        <button
          class="navbar-toggler"
          type="button"
          :aria-expanded="isMenuOpen ? 'true' : 'false'"
          aria-controls="drawer-panel"
          aria-label="Abrir menú"
          @click="toggleMenu"
        >
          <span class="navbar-toggler-icon"></span>
        </button>
      </div>
    </div>

    <!-- Backdrop -->
    <transition name="fade">
      <div
        v-if="isMenuOpen"
        class="backdrop"
        aria-hidden="true"
        @click="closeMenu"
      />
    </transition>

    <!-- Drawer -->
    <transition name="slide">
      <aside
        v-if="isMenuOpen"
        id="drawer-panel"
        class="drawer"
        role="dialog"
        aria-modal="true"
        :aria-hidden="isMenuOpen ? 'false' : 'true'"
        tabindex="-1"
        ref="drawerPanel"
        @keydown.esc="closeMenu"
      >
        <div class="drawer-header">
          <h1 class="offcanvas-title" v-if="menuContent">
            {{ menuContent.menuTitle || 'Menú' }}
          </h1>
          <button class="btn-close" aria-label="Cerrar" @click="closeMenu">×</button>
        </div>

        <div class="drawer-body">
          <ul class="navbar-nav" v-if="menuContent">
            <li v-for="item in menuContent.items" :key="item.path" class="nav-item">
              <router-link :to="item.path" class="nav-link" @click="closeMenu">
                {{ item.label }}
              </router-link>
            </li>
          </ul>
        </div>
      </aside>
    </transition>
  </nav>
</template>
<script>
import { navLabels } from '@/assets/data/data.ts'
import AOS from 'aos'
import 'aos/dist/aos.css'

export default {
  name: 'NavBar',
  props: {
    selectedLang: { type: String, required: true }
  },
  data() {
    return {
      isMobile: false,
      isSticky: false,
      isMenuOpen: false,
      routeUnwatch: null,
      lastFocusedEl: null
    }
  },
  computed: {
    labelMap() {
      return this.isMobile
        ? { es: 'ESP', en: 'ENG', de: 'DEU' }
        : { es: 'Español', en: 'English', de: 'Deutsch' }
    },
    menuContent() {
      if (navLabels && navLabels[this.selectedLang]) return navLabels[this.selectedLang]
      return navLabels.es
    }
  },
  mounted() {
    this.checkMobile()
    AOS.init({ duration: 800, once: true })
    setTimeout(function () { AOS.refresh() }, 300)

    window.addEventListener('resize', this.checkMobile)
    window.addEventListener('scroll', this.handleScroll)

    // Cerrar al cambiar de ruta
    this.routeUnwatch = this.$watch(
      function () { return this.$route.fullPath },
      this.onRouteChange
    )
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile)
    window.removeEventListener('scroll', this.handleScroll)
    if (this.routeUnwatch) this.routeUnwatch()
    this.unlockBodyScroll()
    document.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    onRouteChange() {
      if (this.isMenuOpen) this.closeMenu()
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    handleLanguageChange(event) {
      this.$emit('update-lang', event.target.value)
    },
    handleScroll() {
      this.isSticky = window.scrollY > 80
    },
    toggleMenu() {
      if (this.isMenuOpen) this.closeMenu()
      else this.openMenu()
    },
    openMenu() {
      this.lastFocusedEl = document.activeElement
      this.isMenuOpen = true
      const vm = this
      this.$nextTick(function () {
        vm.lockBodyScroll()
        const panel = vm.$refs.drawerPanel
        if (panel && typeof panel.focus === 'function') panel.focus()
        document.addEventListener('keydown', vm.handleKeydown)
      })
    },
    closeMenu() {
      this.isMenuOpen = false
      this.unlockBodyScroll()
      document.removeEventListener('keydown', this.handleKeydown)
      if (this.lastFocusedEl && typeof this.lastFocusedEl.focus === 'function') {
        this.lastFocusedEl.focus()
      }
    },
    handleKeydown(e) {
      if (e && e.key === 'Escape') this.closeMenu()
    },
    lockBodyScroll() {
      var body = document.body
      var gap = window.innerWidth - document.documentElement.clientWidth
      body.style.overflow = 'hidden'
      if (gap > 0) body.style.paddingRight = String(gap) + 'px'
    },
    unlockBodyScroll() {
      var body = document.body
      body.style.overflow = ''
      body.style.paddingRight = ''
    }
  }
}
</script>


<style scoped>
/* --- Navbar --- */
nav {
  background-color: white;
  box-shadow: 0 13px 11px -7px rgba(0, 0, 0, 0.1);
  padding: 0.5rem 1rem;
  min-height: 60px;
  z-index: 1000 !important;
}
.navbar-toggler:focus { box-shadow: none !important; }
.navbar-fixed {
  position: sticky !important;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000 !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  animation: slideDown 0.3s ease-in-out;
}
@keyframes slideDown {
  from { transform: translateY(-100%); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
}

.logo_navbar { width: 200px; margin-left: 30%; }

.language-selector { display: flex; align-items: center; gap: 0.5rem; margin-right: 0.5rem; }
.language-icon { width: 20px; height: 20px; transition: transform 0.3s ease, filter 0.3s ease; }
.language-icon:hover { transform: scale(1.2); filter: brightness(1.5); cursor: pointer; }

.lang-dropdown {
  background-color: white;
  color: black;
  border-radius: 0.5rem;
  font-weight: 500;
  font-size: 0.95rem;
  padding: 0.35rem 1rem;
  border: 1px solid #ccc;
  appearance: none;
  width: 140px;
}

.nav-link {
  position: relative;
  display: inline-block;
  text-decoration: none;
  color: inherit;
  transition: color 0.3s ease;
  padding-bottom: 2px;
}
.nav-link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0%;
  height: 2px;
  background-color: currentColor;
  transition: width 0.3s ease;
}
.nav-link:hover::after { width: 100%; }

/* --- Backdrop + Drawer (custom) --- */
.backdrop {
  position: fixed; inset: 0; background: rgba(0,0,0,.5);
  z-index: 2000;
}
.drawer {
  position: fixed; top: 0; right: 0; height: 100vh; width: min(86vw, 340px);
  background: #fff; box-shadow: -8px 0 24px rgba(0,0,0,.18);
  z-index: 2100; outline: none; display: flex; flex-direction: column;
}
.drawer-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 16px 6px 16px; border-bottom: 1px solid #eee;
}
.drawer-body {
  padding: 12px 16px; overflow-y: auto; flex: 1;
}
.btn-close {
  appearance: none; border: none; background: transparent; font-size: 28px; line-height: 1; cursor: pointer;
}

/* Transiciones */
.fade-enter-active, .fade-leave-active { transition: opacity .2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.slide-enter-active, .slide-leave-active { transition: transform .25s ease; }
.slide-enter-from, .slide-leave-to { transform: translateX(100%); }

/* Responsive */
@media (max-width: 768px) {
  .lang-dropdown { width: 80px; font-size: .8rem; padding: .2rem .5rem; }
}
</style>
